import type React from "react";
import { createContext, useContext, useEffect, useState } from "react";
import type { Session, User } from "../lib/auth";
import { useSession } from "../lib/auth-client";

interface AuthContextType {
	user: User | null;
	session: Session | null;
	isLoading: boolean;
	isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
	const { data: session, isPending } = useSession();
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		// Set loading to false once we have session data (or confirmed no session)
		if (!isPending) {
			setIsLoading(false);
		}
	}, [isPending]);

	const value: AuthContextType = {
		user: session?.user || null,
		session: session || null,
		isLoading: isPending || isLoading,
		isAuthenticated: !!session?.user,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
}
